import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { lastValueFrom, Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { SwalService } from '../../../../common/services/swal.service';
import { AuthService } from '../../../auth/services/auth.service';
import { CurrenciesService } from '../../services/currencies.service';
import { InsuranceTypesService } from '../../services/insurance-types.service';
import {
  CreateInsuranceTypeDto,
  UpdateInsuranceTypeDto,
  InsuranceCategory,
  InsuranceTypeCurrencyDto,
  InsuranceQuestionGroupDto,
  InsuranceTypeCoverageDto,
  CreateInsuranceTypeCoverageDto,
  InsurancePlanCode,
  CreateRequirementBandDto,
  InsuranceRequirementBandDto,
  InsuranceRequirementLimitDto,
  InsuranceRequirementDto,
  InsuranceRequirementAgeRangeDto,
  CreateQuestionGroupLinkDto,
  CurrencyEntryDto,
} from '../../../../core/data/new-dtos/insurance-type.dtos';
import { SharedModule } from '../../../../common/shared.module';
import { BenefitsListComponent } from '../benefits-list/benefits-list.component';
import { CurrencySelectorComponent } from '../currency-selector/currency-selector.component';
import { QuestionGroupSelectorComponent } from '../question-group-selector/question-group-selector.component';
import { CoverageSelectorComponent } from '../coverage-selector/coverage-selector.component';
import { RequirementBandSelectorComponent } from '../requirement-band-selector/requirement-band-selector.component';
import { FormInputDirective } from '../../../../common/directives/form-input.directive';
import { BanksService } from '../../../banks/services/banks.service';
import { SupervisorsService } from '../../../supervisors/services/supervisors.service';
import { BankSummaryDto } from '../../../../core/data/new-dtos/bank.dtos';
import { SeriousIllnessPlanSelectorComponent } from '../serious-illness-plan-selector/serious-illness-plan-selector.component';
import { CoveragePlanSelectorComponent } from '../coverage-plan-selector/coverage-plan-selector.component';
import { CatastrophicMedicalAgePricingSelectorComponent } from '../catastrophic-medical-age-pricing-selector/catastrophic-medical-age-pricing-selector.component';
import { AgeAmountLimitSelectorComponent } from '../age-amount-limit-selector/age-amount-limit-selector.component';

@Component({
  selector: 'app-insurance-type-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    BenefitsListComponent,
    CurrencySelectorComponent,
    QuestionGroupSelectorComponent,
    CoverageSelectorComponent,
    RequirementBandSelectorComponent,
    SeriousIllnessPlanSelectorComponent,
    CoveragePlanSelectorComponent,
    CatastrophicMedicalAgePricingSelectorComponent,
    AgeAmountLimitSelectorComponent,
  ],
  templateUrl: './insurance-type-form.component.html',
  styleUrls: ['./insurance-type-form.component.scss'],
  providers: [SwalService],
})
export class InsuranceTypeFormComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  insuranceTypeId?: number;

  @ViewChildren(FormInputDirective) formInputs!: QueryList<FormInputDirective>;
  @ViewChild(CatastrophicMedicalAgePricingSelectorComponent) catastrophicMedicalAgePricingSelector?: CatastrophicMedicalAgePricingSelectorComponent;


  categoryOptions = [
    { label: 'Vida', value: InsuranceCategory.Life },
    { label: 'Salud', value: InsuranceCategory.Health },
    { label: 'Accidentes', value: InsuranceCategory.Accident },
    { label: 'Otro', value: InsuranceCategory.Other },
  ];

  formulaOptions = [
    {
      label: 'Vida Temporal Nivelado',
      value: InsurancePlanCode.VidaTemporalNivelado,
    },
    {
      label: 'Vida Término Anual Renovable',
      value: InsurancePlanCode.VidaTerminoAnualRenovable,
    },
    {
      label: 'Vida Devolución de Primas',
      value: InsurancePlanCode.VidaDevolucionDePrimas,
    },
    {
      label: 'Seguro Médico Catastrófico',
      value: InsurancePlanCode.SeguroMedicoCatastrofico,
    },
    {
      label: 'Enfermedades Graves',
      value: InsurancePlanCode.EnfermedadesGraves,
    },
    { label: 'Megalife', value: InsurancePlanCode.Megalife },
    {
      label: 'Accidentes Personales (Individual)',
      value: InsurancePlanCode.AccidentesPersonalesIndividual,
    },
    {
      label: 'Accidentes Personales (Colectivo)',
      value: InsurancePlanCode.AccidentesPersonalesColectivo,
    },
    {
      label: 'Últimos Gastos',
      value: InsurancePlanCode.UltimosGastos,
    },
    {
      label: 'Respaldo Colonial',
      value: InsurancePlanCode.RespaldoColonial,
    },
  ];
  currentCurrencyIds: number[] = [];
  allCurrencies: CurrencyEntryDto[] = [];
  selectedCurrencies: CurrencyEntryDto[] = [];
  /** Whole-policy minimum net premium (before tax) per product currency id; null = no minimum. */
  currencyMinPremiums: Record<number, number | null> = {};
  maxAge$!: Observable<number>;
  maxDurationAge$!: Observable<number>;
  banks: BankSummaryDto[] = [];

  constructor(
    private fb: FormBuilder,
    private swal: SwalService,
    private auth: AuthService,
    private insuranceTypesService: InsuranceTypesService,
    private router: Router,
    private route: ActivatedRoute,
    private el: ElementRef,
    private banksService: BanksService,
    private supervisorsService: SupervisorsService,
    private currenciesService: CurrenciesService
  ) { }

  get isSupervisor() {
    return this.auth.isSupervisor;
  }

  get isSuperUser() {
    return this.auth.isSuperUser;
  }

  get showBankSelector() {
    return this.isSuperUser || this.isSupervisor;
  }

  /**
   * Regional users and managers always consult; supervisors and globals
   * consult Colonial products (no bank), which only La Colonial itself may
   * edit. Set once the product is loaded, since that rule depends on its bank.
   */
  readOnly = false;

  /** The caller's own bank choice on this Colonial product (globals only). */
  bankVisible = true;
  savingBankVisibility = false;

  /** The one thing a global may change on a Colonial product. */
  get showBankVisibilityToggle(): boolean {
    return (
      this.readOnly &&
      this.auth.isGlobal &&
      this.isEditMode &&
      this.form.get('bankId')?.value == null
    );
  }

  async toggleBankVisibility(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const visible = input.checked;

    this.savingBankVisibility = true;
    try {
      await lastValueFrom(
        this.insuranceTypesService.setBankVisibility(this.insuranceTypeId!, visible),
      );
      this.bankVisible = visible;
    } catch (e: any) {
      input.checked = this.bankVisible;
      this.swal.error(e?.error?.error ?? 'No se pudo actualizar la visibilidad.');
    } finally {
      this.savingBankVisibility = false;
    }
  }

  get isEnfermedadesGraves() {
    return (
      this.form?.get('formulaId')?.value ===
      InsurancePlanCode.EnfermedadesGraves
    );
  }

  get isSeguroMedicoCatastrofico() {
    return (
      this.form?.get('formulaId')?.value ===
      InsurancePlanCode.SeguroMedicoCatastrofico
    );
  }

  get isRespaldoColonial() {
    return (
      this.form?.get('formulaId')?.value ===
      InsurancePlanCode.RespaldoColonial
    );
  }

  get isUltimosGastos() {
    return (
      this.form?.get('formulaId')?.value ===
      InsurancePlanCode.UltimosGastos
    );
  }

  // ...

  async ngOnInit() {
    // Build the form first: the template binds [formGroup] synchronously as soon as
    // ngOnInit yields at the first await.
    this.form = this.fb.group({
      name: ['', Validators.required],
      lob: [0, Validators.required],
      slob: [0, Validators.required],
      planCode: [null as number | null],
      formulaId: ['', Validators.required],
      icon: [''],
      description: [''],
      category: [InsuranceCategory.Life, Validators.required],
      durationMin: [null],
      durationMax: [null],
      minAge: [0, Validators.required],
      maxAge: [100, Validators.required],
      maxDurationAge: [0, Validators.required],
      childMinAge: [0],
      childMaxAge: [0],
      childMaxDurationAge: [0],
      familyAllowed: [false],
      visible: [true],
      allowBeneficiaries: [false],
      allowDependents: [true],
      requiresOccupation: [false],
      benefits: [[]],
      currencyIds: [[]],
      questionGroupIds: [[]],
      coverages: [[]],
      requirementBands: [[]],
      seriousIllnessPlans: [[]],
      catastrophicMedicalAgePricings: [[]],
      coveragePlans: [[]],
      ageAmountLimits: [[]],
      bankId: [null],
    });

    this.maxAge$ = this.form
      .get('maxAge')!
      .valueChanges.pipe(startWith(this.form.get('maxAge')?.value));

    this.maxDurationAge$ = this.form
      .get('maxDurationAge')!
      .valueChanges.pipe(startWith(this.form.get('maxDurationAge')?.value));

    // Load all currencies and initialize selected
    this.currenciesService.getAll().subscribe(currencies => {
      this.allCurrencies = currencies;
      this.selectedCurrencies = this.allCurrencies.filter(c => this.currentCurrencyIds.includes(c.id));
    });

    // Initialize and subscribe to currencyIds changes to maintain a stable reference
    this.currentCurrencyIds = this.form.get('currencyIds')?.value ?? [];
    this.form.get('currencyIds')?.valueChanges.subscribe((value) => {
      this.currentCurrencyIds = value ?? [];
      this.selectedCurrencies = this.allCurrencies.filter(c => this.currentCurrencyIds.includes(c.id));
    });

    // Role getters and azureId depend on the bank profile fetched after login
    await this.auth.profileReady();

    // Load banks for SuperUser/Supervisor
    if (this.showBankSelector) {
      await this.loadBanks();
      // Supervisor must select a bank (required)
      if (this.isSupervisor) {
        this.form.get('bankId')?.setValidators(Validators.required);
        this.form.get('bankId')?.updateValueAndValidity();
      }
    }

    // Logic for child fields validation
    this.form.get('familyAllowed')!.valueChanges.subscribe((allowed) => {
      const childControls = [
        'childMinAge',
        'childMaxAge',
        'childMaxDurationAge',
      ];
      childControls.forEach((key) => {
        const control = this.form.get(key);
        if (allowed) {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
          control?.setValue(0);
        }
        control?.updateValueAndValidity();
      });
    });

    // A global admin authors products for their own bank only; no selector shown
    if (this.auth.isGlobal) {
      this.form.get('bankId')?.setValue(this.auth.bankProfile?.bankId ?? null);
    }

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode = true;
      this.insuranceTypeId = Number(idParam);
      await this.loadInsuranceType();
    }

    const isColonialProduct = this.isEditMode && this.form.get('bankId')?.value == null;
    this.readOnly = this.auth.isRegional || this.auth.isManager || (isColonialProduct && !this.isSuperUser);

    if (this.readOnly) {
      this.form.disable({ emitEvent: false });
    }
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.formInputs.forEach((input) => input.validateControl());
      if (this.catastrophicMedicalAgePricingSelector) {
        this.catastrophicMedicalAgePricingSelector.markAllAsTouched();
      }

      setTimeout(() => {
        const invalidControl =
          this.el.nativeElement.querySelector('.is-invalid');
        if (invalidControl) {
          invalidControl.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          invalidControl.focus();
        }
      }, 100);
      return;
    }


    const formValue = this.form.value;

    const benefitsArray: string[] = formValue.benefits ?? [];
    const currencyIdsArray: number[] = formValue.currencyIds ?? [];
    const questionGroupLinksArray: CreateQuestionGroupLinkDto[] = formValue.questionGroupIds ?? [];
    const coveragesArray: CreateInsuranceTypeCoverageDto[] =
      formValue.coverages ?? [];
    const requirementBandsArray: CreateRequirementBandDto[] =
      formValue.requirementBands ?? [];
    const seriousIllnessPlansArray: any[] = formValue.seriousIllnessPlans ?? [];

    if (this.isEnfermedadesGraves && seriousIllnessPlansArray.length === 0) {
      this.swal.error('Debes agregar al menos un plan.');
      return;
    }

    const coveragePlansArray: any[] = formValue.coveragePlans ?? [];
    if ((this.isRespaldoColonial || this.isUltimosGastos) && coveragePlansArray.length === 0) {
      this.swal.error('Debes agregar al menos un plan.');
      return;
    }

    if (!this.isEnfermedadesGraves && !this.isRespaldoColonial && !this.isUltimosGastos && currencyIdsArray.length === 0) {
      this.swal.error('Debes seleccionar al menos una moneda.');
      return;
    }

    if (!this.isEnfermedadesGraves && !this.isRespaldoColonial && !this.isUltimosGastos && coveragesArray.length === 0) {
      this.swal.error('Debes agregar al menos una cobertura.');
      return;
    }

    coveragesArray.forEach((c) => {
      c.currencies.forEach((curr) => {
        if (!curr.minAmount) curr.minAmount = 0;
        if (!curr.maxAmount) curr.maxAmount = 0;
        if (curr.isFixedLimit === undefined) curr.isFixedLimit = false;
      });
    });

    const bankId: number | null = formValue.bankId ?? null;

    const dto: CreateInsuranceTypeDto | UpdateInsuranceTypeDto = {
      lob: formValue.lob,
      slob: formValue.slob,
      planCode: formValue.planCode === '' || formValue.planCode == null ? null : Number(formValue.planCode),
      formulaId: formValue.formulaId,
      name: formValue.name,
      icon: formValue.icon,
      description: formValue.description,
      category: formValue.category,
      durationMin: formValue.durationMin === '' ? null : formValue.durationMin,
      durationMax: formValue.durationMax === '' ? null : formValue.durationMax,
      minAge: formValue.minAge,
      maxAge: formValue.maxAge,
      maxDurationAge: formValue.maxDurationAge,
      childMinAge: formValue.childMinAge,
      childMaxAge: formValue.childMaxAge,
      childMaxDurationAge: formValue.childMaxDurationAge,
      familyAllowed: formValue.familyAllowed,
      visible: formValue.visible ?? true,
      // Policy beneficiaries (name, document, relationship, %) are requested at checkout when enabled.
      allowBeneficiaries: !!formValue.allowBeneficiaries,
      // Family members are always dependents.
      allowDependents: true,
      requiresOccupation: formValue.requiresOccupation,
      benefits: benefitsArray,
      bankId: bankId,
      currencyIds: currencyIdsArray,
      questionGroupIds: [],
      questionGroupLinks: questionGroupLinksArray,
      coverages: coveragesArray,
      requirementBands: requirementBandsArray,
      seriousIllnessPlans: seriousIllnessPlansArray,
      catastrophicMedicalAgePricings: formValue.catastrophicMedicalAgePricings ?? [],
      coveragePlans: coveragePlansArray,
      currencyMinPremiums: currencyIdsArray.map((currencyId: number) => ({
        currencyId,
        minPremium: this.currencyMinPremiums[currencyId] ?? null,
      })),
      ageAmountLimits: formValue.ageAmountLimits ?? [],
    };

    this.swal.showLoading(this.isEditMode ? 'Actualizando...' : 'Creando...');
    try {
      if (this.isEditMode) {
        await lastValueFrom(
          this.insuranceTypesService.update(this.insuranceTypeId!, dto),
        );
        await this.swal.success('Producto actualizado.');
      } else {
        await lastValueFrom(
          this.insuranceTypesService.create(dto as CreateInsuranceTypeDto),
        );
        await this.swal.success('Producto creado.');
      }
      this.router.navigate(['/insurance-types']);
    } catch (e: any) {
      const serverError = e?.error?.error;
      this.swal.error(serverError || 'Ocurrió un error al guardar el producto.');
    }
  }

  updateCurrencyMinPremium(currencyId: number, value: number | string | null): void {
    this.currencyMinPremiums[currencyId] = value === '' || value == null ? null : Number(value);
  }

  cancel() {
    this.router.navigate(['/insurance-types']);
  }

  private async loadBanks() {
    try {
      if (this.isSupervisor && this.auth.azureId) {
        // Supervisors only see the banks derived from their branch assignments
        const branches = await lastValueFrom(
          this.supervisorsService.getBranches(this.auth.azureId),
        );
        this.banks = [...new Map(branches.map((b) => [b.bank.id, b.bank])).values()];
      } else {
        this.banks = await lastValueFrom(this.banksService.getBanks());
      }
    } catch {
      this.banks = [];
    }
  }

  private mapCategory(category: any): InsuranceCategory {
    if (typeof category === 'number') return category;
    if (typeof category === 'string') {
      switch (category.toLowerCase()) {
        case 'life':
          return InsuranceCategory.Life;
        case 'health':
          return InsuranceCategory.Health;
        case 'accident':
          return InsuranceCategory.Accident;
        case 'other':
          return InsuranceCategory.Other;
        default:
          return InsuranceCategory.Life;
      }
    }
    return InsuranceCategory.Life;
  }

  private async loadInsuranceType() {
    this.swal.showLoading('Cargando producto...');
    try {
      const data = await lastValueFrom(
        this.insuranceTypesService.getById(this.insuranceTypeId!),
      );
      this.bankVisible = data.visibleForBank ?? true;
      this.form.patchValue({
        name: data.name,
        lob: data.lob,
        slob: data.slob,
        planCode: data.planCode ?? null,
        formulaId: data.formulaId,
        icon: data.icon,
        description: data.description,
        category: this.mapCategory(data.category),
        durationMin: data.durationMin,
        durationMax: data.durationMax,
        minAge: data.minAge,
        maxAge: data.maxAge,
        maxDurationAge: data.maxDurationAge,
        childMinAge: data.childMinAge ?? 0,
        childMaxAge: data.childMaxAge ?? 0,
        childMaxDurationAge: data.childMaxDurationAge ?? 0,
        familyAllowed: data.familyAllowed,
        visible: data.visible ?? true,
        allowBeneficiaries: data.allowBeneficiaries ?? false,
        allowDependents: data.allowDependents ?? true,
        requiresOccupation: data.requiresOccupation,
        benefits: data.benefits ?? [],
        currencyIds:
          data.currencies?.map((c: InsuranceTypeCurrencyDto) => c.id) ?? [],
        questionGroupIds:
          data.questionGroups?.map((q: InsuranceQuestionGroupDto) => ({
            questionGroupId: q.id,
            phase: q.phase ?? 'Checkout',
          })) ?? [],
        coverages:
          data.coverages?.map((c: InsuranceTypeCoverageDto) => ({
            coverageId: c.id,
            isRequired: c.isRequired,
            insurePrimaryFraction: c.insurePrimaryFraction,
            maxAge: c.maxAge,
            maxDurationAge: c.maxDurationAge,
            isCalculated: c.isCalculated ?? false,
            tariffType: c.tariffType,
            currencies:
              c.currencies?.map((curr) => ({
                currencyId: curr.id,
                minAmount: curr.minAmount ?? 0,
                maxAmount: curr.maxAmount ?? 0,
                isFixedLimit: curr.isFixedLimit ?? false,
                minPremium: curr.minPremium ?? null,
              })) ?? [],
            categories: c.categories?.map((cat: any) => ({
              category: cat.category,
              rates: cat.rates?.map((r: any) => ({
                ageFrom: r.ageFrom,
                ageTo: r.ageTo,
                rate: r.rate
              })) ?? []
            })) ?? []
          })) ?? [],
        bankId: (data as any).bankId ?? null,
        requirementBands:
          data.requirementBands?.map((b: InsuranceRequirementBandDto) => ({
            ageRanges:
              b.ageRanges?.map((ar: InsuranceRequirementAgeRangeDto) => ({
                ageFrom: ar.ageFrom,
                ageTo: ar.ageTo,
              })) ?? [],
            limits:
              b.limits?.map((l: InsuranceRequirementLimitDto) => ({
                currency: l.currency,
                amountFrom: l.amountFrom,
                amountTo: l.amountTo,
              })) ?? [],
            requirements:
              b.requirements?.map((r: InsuranceRequirementDto) => ({
                name: r.name,
              })) ?? [],
          })) ?? [],
        seriousIllnessPlans:
          data.seriousIllnessPlans?.map((p) => ({
            name: p.name,
            diagnosisPayout: p.diagnosisPayout,
            survivalPayout: p.survivalPayout,
            survivalPeriodMonths: p.survivalPeriodMonths,
            premiums:
              p.premiums?.map((pr) => ({
                minAge: pr.minAge,
                maxAge: pr.maxAge,
                amount: pr.amount,
              })) ?? [],
            coverageIds: p.coverages?.map((c) => c.coverageId) ?? [],
          })) ?? [],
        catastrophicMedicalAgePricings:
          data.catastrophicMedicalAgePricings?.map((p: any) => ({
            id: p.id,
            personInsuranceCurrencyId: p.personInsuranceCurrencyId,
            minAge: p.minAge,
            maxAge: p.maxAge,
            gender: p.gender,
            isForChild: p.isForChild,
            premiumAmount: p.premiumAmount,
          })) ?? [],
        coveragePlans:
          (data as any).coveragePlans?.map((cp: any) => ({
            name: cp.name,
            items:
              cp.items?.map((item: any) => ({
                coverageName: item.coverageName,
                coverageId: item.coverageId,
                sumInsured: item.sumInsured,
                premium: item.premium,
              })) ?? [],
          })) ?? [],
        ageAmountLimits: data.ageAmountLimits ?? [],
      });
      this.currencyMinPremiums = Object.fromEntries(
        (data.currencies ?? []).map((c: InsuranceTypeCurrencyDto) => [c.id, c.minPremium ?? null]),
      );
      this.swal.dismiss();
    } catch (e) {
      this.swal.error('No se pudo cargar el producto.');
      this.router.navigate(['/insurance-types']);
    }
  }
}
